import { stringFormatter } from "@kivunova/kivufrontendcommon";
import { Category, CategoryNode } from "../types/categoryTypes";
import type { Product } from "../../Products/types/products";

const getProductCategoryId = (product: Product): string | undefined => {
  const raw = product.category ?? product.productCategory;
  if (raw == null) return undefined;
  const id = String(raw).trim();
  return id.length > 0 ? id : undefined;
};

/**
 * Count products per category, including all descendants.
 * Direct assignments are rolled up to every ancestor category.
 */
export const enrichCategoriesWithProductCount = (
  categories: Category[],
  productList: Product[]
): Category[] => {
  const directCountByCategoryId = new Map<string, number>();
  const totalCountByCategoryId = new Map<string, number>();
  const parentByCategoryId = new Map(
    categories.map((category) => [category.id, category.parentId ?? null])
  );

  for (const category of categories) {
    totalCountByCategoryId.set(category.id, 0);
  }

  for (const product of productList) {
    const categoryId = getProductCategoryId(product);
    if (!categoryId) continue;
    directCountByCategoryId.set(
      categoryId,
      (directCountByCategoryId.get(categoryId) ?? 0) + 1
    );
  }

  for (const [categoryId, directCount] of directCountByCategoryId) {
    if (!totalCountByCategoryId.has(categoryId)) continue;

    totalCountByCategoryId.set(
      categoryId,
      (totalCountByCategoryId.get(categoryId) ?? 0) + directCount
    );

    let parentId = parentByCategoryId.get(categoryId) ?? null;
    while (parentId) {
      totalCountByCategoryId.set(
        parentId,
        (totalCountByCategoryId.get(parentId) ?? 0) + directCount
      );
      parentId = parentByCategoryId.get(parentId) ?? null;
    }
  }

  return categories.map((category) => ({
    ...category,
    directProductCount: directCountByCategoryId.get(category.id) ?? 0,
    productCount: totalCountByCategoryId.get(category.id) ?? 0
  }));
};

/** Format path for display: each segment via common stringFormatter (e.g. "parent/child" -> "Parent / Child"). */
export const formatCategoryPath = (path: string | undefined | null): string => {
  if (path == null || path === "") return "-";
  return path
    .split("/")
    .filter(Boolean)
    .map((segment) => stringFormatter(segment.trim()))
    .join(" / ");
};

/** Returns only leaf categories (no children) for product filters etc. */
export const getLeafCategories = (categories: Category[]): Category[] => {
  const parentIds = new Set(
    categories.filter((c) => c.parentId).map((c) => c.parentId as string)
  );
  return categories.filter((cat) => !parentIds.has(cat.id));
};

export const buildCategoryTree = (categories: Category[]): CategoryNode[] => {
  const map = new Map<string, CategoryNode>();

  // Initialize all categories with empty children arrays
  categories.forEach((c) => map.set(c.id, { ...c, children: [] }));

  const roots: CategoryNode[] = [];

  // Connect parent → child
  map.forEach((cat) => {
    if (cat.parentId && map.has(cat.parentId)) {
      map.get(cat.parentId)!.children.push(cat);
    } else {
      roots.push(cat);
    }
  });

  return roots;
};
